import { useEffect, useRef, useState } from "react";
import _ from "lodash";

const COMMIT_DEBOUNCE_MS = 1000;

/**
 * Shared behaviour for text editors that can preview live.
 *
 * With `livePreview` (tree-view containers): every keystroke patches the
 * playable directly, while the expensive React state + persist commit is
 * debounced (1s) and flushed on blur. Without it, the historical
 * commit-on-every-keystroke behaviour is kept untouched.
 *
 * @param {object} params
 * @param {*} params.value committed value coming from the version
 * @param {Function} params.setValue full commit (state + undo + persist)
 * @param {Function} [params.livePreview] drag-time playable patch
 * @returns {{text: string, onChange: Function, onFocus: Function, onBlur: Function, cancel: Function}}
 */
export function useLiveTextValue({ value, setValue, livePreview }) {
  const hasLivePreview = _.isFunction(livePreview);
  const [text, setText] = useState(_.isNil(value) ? "" : value);
  const isEditingRef = useRef(false);

  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;
  const livePreviewRef = useRef(livePreview);
  livePreviewRef.current = livePreview;

  const commitRef = useRef(null);
  if (!commitRef.current) {
    commitRef.current = _.debounce((next) => setValueRef.current(next), COMMIT_DEBOUNCE_MS);
  }

  useEffect(() => {
    // While the user types, the local text is the source of truth: syncing here
    // would rewind the field to whatever the debounced commit last wrote.
    if (hasLivePreview && isEditingRef.current) return;
    setText(_.isNil(value) ? "" : value);
  }, [value]);

  useEffect(() => {
    const commit = commitRef.current;
    return () => commit.flush();
  }, []);

  const onChange = (next) => {
    setText(next);
    if (!hasLivePreview) {
      setValueRef.current(next);
      return;
    }
    livePreviewRef.current(next);
    commitRef.current(next);
  };

  const onFocus = () => {
    isEditingRef.current = true;
  };

  const onBlur = () => {
    isEditingRef.current = false;
    commitRef.current.flush();
  };

  const cancel = () => {
    isEditingRef.current = false;
    commitRef.current.cancel();
  };

  return { text, onChange, onFocus, onBlur, cancel };
}
